import { formatDecimalBR } from "@/lib/format-decimal-br"
import type {
  ActiveBenefits,
  Benefit,
  BenefitAvailability,
  CartThresholdConfig,
  ItemSpendPromo,
  LoyaltyBonus,
  PointsProgramSummary,
  RecurrenceConfig,
  StackingPolicy,
} from "@/types/loyalty-benefits"

/**
 * BENEFIT SHOWCASE (home section "Benefícios da loja")
 *
 * Turns the merchant's active loyalty mechanics into customer-facing cards:
 * one card per mechanic, a title and a few sentences each. Pure functions —
 * the carousel only lays the cards out and decides the "valid now" state
 * against the browser clock. MIRRORED in merchant-portal-web
 * (`store-front/benefit-showcase.ts`) so the appearance preview draws the
 * merchant's own benefits with the same words.
 */

export type BenefitShowcaseKind = "points" | "cartThreshold" | "itemPromo" | "recurrence"

export type BenefitShowcaseCard = {
  id: string
  kind: BenefitShowcaseKind
  title: string
  /** Sentences, in reading order; each already ends with a period. */
  lines: string[]
  /** Weekly window; the card decides whether it is open NOW. */
  availability?: BenefitAvailability | null
  /** Campaign end as a footnote ("Campanha até 30/09/2026"); points programs only. */
  footnote?: string
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  PIX: "Pix",
  PIX_ONLINE: "Pix",
  CREDIT: "crédito",
  DEBIT: "débito",
  CASH: "dinheiro",
  CREDIT_DEBIT: "crédito ou débito",
}

function money(value: number): string {
  return `R$ ${formatDecimalBR(value)}`
}

function plural(count: number, singular: string, pluralForm: string): string {
  return count === 1 ? singular : pluralForm
}

function formatPoints(points: number): string {
  return points.toLocaleString("pt-BR", { maximumFractionDigits: 2 })
}

const PERCENT_CHANGE = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 })

/** Loyalty multiplier read as its percent gain over the 1x baseline (`2` → `+100%`): the signed-percent rule every relative change uses, never a "Nx" label. */
function formatFactor(factor: number): string {
  const change = (factor - 1) * 100
  const sign = change > 0 ? "+" : change < 0 ? "-" : ""
  return `${sign}${PERCENT_CHANGE.format(Math.abs(change))}%`
}

function joinList(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? ""
  return `${parts.slice(0, -1).join(", ")} ou ${parts[parts.length - 1]}`
}

/** "DD/MM/YYYY" from a civil "YYYY-MM-DD"; the input echoed back when it is not one. */
function formatCivilDate(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date)
  return match ? `${match[3]}/${match[2]}/${match[1]}` : date
}

/** Lower-case phrase for a benefit, meant mid-sentence ("...e ganhe frete grátis"). */
export function describeBenefit(b: Benefit): string {
  if (b.freeProduct) return `${b.freeProduct.item.name?.trim() || "um produto"} grátis`
  if (b.orderDiscount) return `${money(b.orderDiscount.amount.value)} de desconto`
  if (b.shipping) {
    return b.shipping.type === "FREE_SHIPPING"
      ? "frete grátis"
      : `${money(b.shipping.amount?.value ?? 0)} de desconto no frete`
  }
  return "um benefício"
}

function bonusReward(bonus: LoyaltyBonus): string | null {
  const reward = bonus.reward
  if (reward?.type === "FIXED_POINTS" && (reward.points ?? 0) > 0) {
    const points = reward.points ?? 0
    return `${formatPoints(points)} ${plural(points, "ponto", "pontos")} a mais`
  }
  if (reward?.type === "MULTIPLIER" && (reward.factor ?? 0) > 0) {
    return `${formatFactor(reward.factor ?? 0)} de pontos`
  }
  return null
}

function bonusLine(bonus: LoyaltyBonus): string | null {
  const reward = bonusReward(bonus)
  if (!reward) return null
  const earns = bonus.reward.type === "FIXED_POINTS" ? `ganham ${reward}` : `valem ${reward}`

  switch (bonus.type) {
    case "FIRST_ORDER":
      return bonus.reward.type === "FIXED_POINTS"
        ? `Novos clientes ganham ${reward} no primeiro pedido.`
        : `O primeiro pedido vale ${reward}.`
    case "MIN_ORDER_VALUE":
      if (!bonus.minOrderValue || bonus.minOrderValue <= 0) return null
      return `Pedidos acima de ${money(bonus.minOrderValue)} ${earns}.`
    case "PAYMENT_METHOD": {
      const methods = (bonus.paymentMethods ?? []).map((m) => PAYMENT_METHOD_LABELS[m] ?? m.toLowerCase())
      if (methods.length === 0) return null
      return `Pagando com ${joinList(methods)}, os pedidos ${earns}.`
    }
    case "ITEMS": {
      const names = (bonus.items ?? []).map((item) => item.name?.trim() ?? "").filter(Boolean)
      const subject = names.length > 0 ? `Pedidos com ${joinList(names)}` : "Pedidos com os itens participantes"
      return `${subject} ${earns}.`
    }
    default:
      return null
  }
}

function pointsCard(program: PointsProgramSummary): BenefitShowcaseCard | null {
  const lines: string[] = []
  const rate = program.pointsPerCurrency

  if (rate >= 1) {
    lines.push(`A cada R$ 1,00 em compras você ganha ${formatPoints(rate)} ${plural(rate, "ponto", "pontos")}.`)
  } else if (rate > 0) {
    lines.push(`A cada ${money(1 / rate)} em compras você ganha 1 ponto.`)
  }

  if (!program.redemptionPaused && program.pointsValueInCurrency > 0) {
    const minimum = program.minimumPointsToRedeem
    lines.push(
      minimum > 0
        ? `Junte ${formatPoints(minimum)} pontos e troque por ${money(minimum * program.pointsValueInCurrency)} em recompensas.`
        : `Cada ponto vale ${money(program.pointsValueInCurrency)} na hora de trocar.`,
    )
  }

  for (const bonus of program.bonuses ?? []) {
    if (!bonus.enabled) continue
    const line = bonusLine(bonus)
    if (line) lines.push(line)
  }

  const description = program.description?.trim()
  if (description) lines.push(description)

  if (lines.length === 0) return null

  const endsAt = program.expiration?.type === "FIXED_PERIOD" ? program.expiration.endsAt : undefined

  return {
    id: `points:${program.programId}`,
    kind: "points",
    title: program.name?.trim() || "Programa de pontos",
    lines,
    availability: program.availability,
    footnote: endsAt ? `Campanha válida até ${formatCivilDate(endsAt)}` : undefined,
  }
}

function cartThresholdCard(config: CartThresholdConfig): BenefitShowcaseCard {
  return {
    id: `cart-threshold:${config.id}`,
    kind: "cartThreshold",
    title: config.name?.trim() || "Compre mais e ganhe",
    lines: [
      `Em compras a partir de ${money(config.minItemsSubtotal.value)} você ganha ${describeBenefit(config.benefit)}.`,
    ],
    availability: config.availability,
  }
}

function itemPromoCard(promo: ItemSpendPromo): BenefitShowcaseCard | null {
  const item = promo.triggerItem.name?.trim() || "o item participante"
  const tiers = [...promo.tiers].sort((a, b) => a.minItemSpend.value - b.minItemSpend.value)
  if (tiers.length === 0) return null

  const lines = tiers.map((tier) =>
    promo.basis === "ACCUMULATED"
      ? `Some ${money(tier.minItemSpend.value)} em ${item} ao longo dos seus pedidos e ganhe ${describeBenefit(tier.benefit)}.`
      : `Gaste ${money(tier.minItemSpend.value)} em ${item} no pedido e ganhe ${describeBenefit(tier.benefit)}.`,
  )

  return {
    id: `item-promo:${promo.id}`,
    kind: "itemPromo",
    title: promo.name?.trim() || `Promoção de ${item}`,
    lines,
    availability: promo.availability,
  }
}

function recurrenceCard(config: RecurrenceConfig): BenefitShowcaseCard {
  const item = config.reward.item.name?.trim() || "um produto"
  const orders = config.requiredOrders
  const line =
    config.policy === "ONE_TIME"
      ? `No seu ${orders}º pedido você ganha ${item} grátis.`
      : config.policy === "CONTINUOUS"
        ? `A cada ${orders} pedidos você ganha ${item} grátis, quantas vezes completar.`
        : `A cada ${orders} pedidos você ganha ${item} grátis.`

  return {
    id: `recurrence:${config.id}`,
    kind: "recurrence",
    title: config.name?.trim() || "Cartão fidelidade",
    lines: [line],
    availability: config.availability,
  }
}

/**
 * Cards in showcase order: points program, cart threshold, item promos,
 * recurrence — the same order the merchant sees in Configurações → Fidelidade.
 * Inactive configs and mechanics with nothing to say are skipped.
 */
export function buildBenefitShowcase(active: ActiveBenefits | null | undefined): BenefitShowcaseCard[] {
  if (!active) return []
  const cards: BenefitShowcaseCard[] = []

  if (active.pointsProgram) {
    const card = pointsCard(active.pointsProgram)
    if (card) cards.push(card)
  }
  if (active.cartThreshold?.active) cards.push(cartThresholdCard(active.cartThreshold))
  for (const promo of active.itemPromos ?? []) {
    if (!promo.active) continue
    const card = itemPromoCard(promo)
    if (card) cards.push(card)
  }
  if (active.recurrence?.active) cards.push(recurrenceCard(active.recurrence))

  return cards
}

/**
 * The merchant-wide rule, worth stating only when the customer could combine
 * something: two or more mechanics besides points (points redemption is
 * always allowed alongside, so a points card alone settles nothing).
 */
export function stackingRuleCopy(policy: StackingPolicy, cards: BenefitShowcaseCard[]): string | null {
  const combinable = cards.filter((card) => card.kind !== "points").length
  if (combinable < 2) return null
  return policy === "SINGLE_BENEFIT_PER_ORDER"
    ? "Vale um benefício por pedido. O resgate de pontos pode ser somado."
    : "Os benefícios podem ser combinados no mesmo pedido."
}
